import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BasicInfoComponent } from '../basic-info/basic-info.component';
import { MetadataInfoComponent } from '../metadata-info/metadata-info.component';
import type {
  OgcOnboardingMode,
  ResourceServerId,
} from '../../dataset-home/dataset-home.models';
import { ResourceServerSummaryComponent } from '../../resource-server-summary/resource-server-summary.component';
import { NgsildItemService } from '../ngsild-item.service';
import { CatalogueApiService } from '@angular-workspace/services';
import {
  getApiErrorDetail,
  getApiErrorStatus,
} from '../ngsild-api-error.util';
import { clearDataDescriptorDraft } from '../data-descriptor.logic';
import type {
  NgsildBasicInfo,
  NgsildMetadataInfo,
  NgsildOnboardingResult,
} from '../ngsild.models';
import { StacCollectionOnboardingComponent } from '../../ogc/stac-collection-onboarding/stac-collection-onboarding.component';
import {
  isOgcRasterOnboarding,
  maxWizardStep,
  OGC_RASTER_STAC_STEPS,
  parseWizardStepParam,
} from '../../ogc/ogc-raster-wizard.util';

const STEP_QUERY_KEY = 'step';

interface NgsildStep {
  key: string;
  label: string;
}

const BASE_STEPS: NgsildStep[] = [
  { key: 'basic', label: 'Basic Information' },
  { key: 'metadata', label: 'Metadata Information' },
];

@Component({
  selector: 'lib-ngsild-home',
  standalone: true,
  imports: [
    CommonModule,
    BasicInfoComponent,
    MetadataInfoComponent,
    ResourceServerSummaryComponent,
    StacCollectionOnboardingComponent,
  ],
  templateUrl: './ngsild-home.component.html',
  styleUrl: './ngsild-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgsildHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngsildItemService = inject(NgsildItemService);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly resourceServer = input<ResourceServerId>('ngsi-ld');
  readonly ogcOnboardingMode = input<OgcOnboardingMode | null | undefined>(null);
  readonly publisherRole = input<'org-admin' | 'provider'>('provider');

  readonly basicInfoRef = viewChild(BasicInfoComponent);
  readonly metadataInfoRef = viewChild(MetadataInfoComponent);
  readonly stacRef = viewChild(StacCollectionOnboardingComponent);

  readonly back = output<void>();
  readonly complete = output<NgsildOnboardingResult>();
  /** Emitted when item create fails (e.g. 409 name conflict); host shows toaster. */
  readonly submitError = output<string>();

  readonly isSubmitting = signal(false);

  readonly isOgcRaster = computed(() =>
    isOgcRasterOnboarding(this.resourceServer(), this.ogcOnboardingMode()),
  );

  readonly isOgcFeature = computed(
    () => this.resourceServer() === 'ogc' && this.ogcOnboardingMode() === 'feature',
  );

  readonly steps = computed(() => {
    if (!this.isOgcRaster()) {
      return BASE_STEPS;
    }
    return [...BASE_STEPS, ...OGC_RASTER_STAC_STEPS];
  });

  readonly currentStep = signal(0);
  readonly basicValid = signal(false);
  readonly metadataValid = signal(false);
  readonly basicInfo = signal<NgsildBasicInfo | null>(null);
  readonly metadataInfo = signal<NgsildMetadataInfo | null>(null);
  readonly createdItemId = signal<string | null>(null);
  readonly stacCollectionId = signal<string | null>(null);
  readonly createdDatasetResult = signal<NgsildOnboardingResult | null>(null);

  readonly canGoNext = computed(() => {
    if (this.isSubmitting()) {
      return false;
    }
    const step = this.currentStep();
    if (step === 0) {
      return this.basicValid();
    }
    if (step === 1) {
      return this.metadataValid();
    }
    if (step === 2) {
      const stac = this.stacRef();
      return !!stac && stac.canSubmitCollection() && !stac.submitting();
    }
    if (step === 3) {
      const stac = this.stacRef();
      return !!stac && stac.canSubmitItem() && !stac.itemSubmitting();
    }
    return false;
  });

  readonly isLastStep = computed(
    () => this.currentStep() === this.steps().length - 1,
  );

  ngOnInit(): void {
    const maxStep = maxWizardStep(this.isOgcRaster());
    const raw = this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY);
    this.currentStep.set(parseWizardStepParam(raw, maxStep));

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const step = parseWizardStepParam(
          params.get(STEP_QUERY_KEY),
          maxWizardStep(this.isOgcRaster()),
        );
        if (this.currentStep() !== step) {
          this.currentStep.set(step);
        }
      });
  }

  onBasicValidChange(valid: boolean): void {
    this.basicValid.set(valid);
  }

  onBasicValueChange(value: NgsildBasicInfo): void {
    this.basicInfo.set(value);
  }

  onMetadataValidChange(valid: boolean): void {
    this.metadataValid.set(valid);
  }

  onMetadataValueChange(value: NgsildMetadataInfo): void {
    this.metadataInfo.set(value);
  }

  isStepActive(index: number): boolean {
    return this.currentStep() === index;
  }

  isStepCompleted(index: number): boolean {
    return index < this.currentStep();
  }

  /** False when the user is on the OGC Raster item step after a STAC collection has been created.
   *  Once a collection exists the collection step is permanently locked and there is nothing
   *  to go back to, so the Previous button must be hidden. */
  canGoBack(): boolean {
    if (this.isOgcRaster() && this.currentStep() === 3 && !!this.stacCollectionId()) {
      return false;
    }
    return true;
  }

  isStepClickable(index: number): boolean {
    if (index <= this.currentStep()) {
      // Once a STAC collection is created, the collection step (2) is locked.
      if (this.isOgcRaster() && index === 2 && !!this.stacCollectionId()) {
        return false;
      }
      return true;
    }
    if (!this.isOgcRaster()) {
      return false;
    }
    if (index === 2) {
      return !!this.createdItemId();
    }
    if (index === 3) {
      return !!this.stacCollectionId();
    }
    return false;
  }

  onStepClick(index: number): void {
    if (!this.isStepClickable(index)) {
      return;
    }
    this.currentStep.set(index);
    this.syncStepQueryParam();
  }

  onBackClick(): void {
    if (this.currentStep() > 0) {
      let prevStep = this.currentStep() - 1;
      // Skip the locked collection step (2) when the STAC collection has already been created.
      if (this.isOgcRaster() && prevStep === 2 && !!this.stacCollectionId()) {
        prevStep = 1;
      }
      this.currentStep.set(prevStep);
      this.syncStepQueryParam();
      return;
    }
    this.back.emit();
  }

  async onNextClick(): Promise<void> {
    const step = this.currentStep();

    if (step === 0) {
      this.basicInfoRef()?.form.markAllAsTouched();
      if (!this.canGoNext()) {
        return;
      }
      this.currentStep.update((s) => s + 1);
      this.syncStepQueryParam();
      return;
    }

    if (step === 1) {
      this.metadataInfoRef()?.form.markAllAsTouched();
      if (!this.canGoNext()) {
        return;
      }
      if (!this.metadataInfoRef()?.validateAdditionalInfo()) {
        return;
      }

      if (this.isOgcRaster()) {
        if (!this.createdItemId()) {
          await this.createDatasetItem();
          if (!this.createdItemId()) {
            return;
          }
        }
        this.currentStep.set(2);
        this.syncStepQueryParam();
        return;
      }

      await this.submitAndComplete();
      return;
    }

    if (step === 2 && this.isOgcRaster()) {
      await this.stacRef()?.submitCollection();
      return;
    }

    if (step === 3 && this.isOgcRaster()) {
      await this.stacRef()?.submitItem();
    }
  }

  onStacCollectionStepComplete(event: { id: string }): void {
    this.stacCollectionId.set(event.id);
    this.currentStep.set(3);
    this.syncStepQueryParam();
  }

  onStacItemStepComplete(event: { collectionId: string; itemId: string }): void {
    const result = this.createdDatasetResult();
    if (!result) {
      return;
    }
    clearDataDescriptorDraft();
    this.resetWizard();
    this.complete.emit({
      ...result,
      stacCollectionId: event.collectionId,
      stacItemId: event.itemId,
    });
  }

  onStacOnboardingError(message: string): void {
    this.submitError.emit(message);
  }

  nextButtonLabel(): string {
    if (this.isSubmitting()) {
      if (this.currentStep() === 1 && this.isOgcRaster()) {
        return 'Creating dataset…';
      }
      return 'Submitting…';
    }

    const step = this.currentStep();
    if (this.isOgcRaster()) {
      if (step === 1) {
        return 'Next →';
      }
      if (step === 2) {
        const stac = this.stacRef();
        return stac?.submitting() ? 'Creating...' : 'Create Collection';
      }
      if (step === 3) {
        const stac = this.stacRef();
        if (stac?.itemSubmitting()) {
          return 'Creating...';
        }
        return 'Create STAC Item';
      }
    }

    return this.isLastStep() ? 'Submit' : 'Next →';
  }

  /** Prefer live form values (both steps stay mounted); fall back to cached signals. */
  private buildBasicPayload(): NgsildBasicInfo | null {
    const ref = this.basicInfoRef();
    if (ref) {
      return ref.form.getRawValue() as NgsildBasicInfo;
    }
    return this.basicInfo();
  }

  private buildMetadataPayload(): NgsildMetadataInfo | null {
    const ref = this.metadataInfoRef();
    if (ref) {
      const raw = ref.form.getRawValue() as NgsildMetadataInfo;
      return {
        ...raw,
        dataDescriptors: ref.getDescriptorsData(),
        dataSample: ref.getSampleData(),
      };
    }
    return this.metadataInfo();
  }

  private async createDatasetItem(): Promise<void> {
    const basic = this.buildBasicPayload();
    const metadata = this.buildMetadataPayload();
    if (!basic || !metadata) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const result = await this.ngsildItemService.createDatasetItem(
        { basic, metadata },
        this.resourceServer(),
        this.ogcOnboardingMode() ?? null,
      );
      this.createdItemId.set(result.itemId);
      this.createdDatasetResult.set({
        payload: result.payload,
        itemId: result.itemId,
        resourceServerId: result.resourceServerId,
        resourceServerKeys: result.resourceServerKeys,
      });
      await this.submitAdditionalInfo(result.itemId);
    } catch (error) {
      console.error('Failed to create dataset item:', error);
      const status = getApiErrorStatus(error);
      const detail = getApiErrorDetail(
        error,
        status === 409
          ? 'Item with this name already exists in local catalogue'
          : 'Failed to create dataset. Please try again.',
      );
      this.submitError.emit(detail);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async submitAndComplete(): Promise<void> {
    const basic = this.buildBasicPayload();
    const metadata = this.buildMetadataPayload();
    if (!basic || !metadata) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const result = await this.ngsildItemService.createDatasetItem(
        { basic, metadata },
        this.resourceServer(),
        this.ogcOnboardingMode() ?? null,
      );
      await this.submitAdditionalInfo(result.itemId);
      clearDataDescriptorDraft();
      this.resetWizard();
      this.complete.emit({
        payload: result.payload,
        itemId: result.itemId,
        resourceServerId: result.resourceServerId,
        resourceServerKeys: result.resourceServerKeys,
      });
    } catch (error) {
      console.error('Failed to create dataset item:', error);
      const status = getApiErrorStatus(error);
      const detail = getApiErrorDetail(
        error,
        status === 409
          ? 'Item with this name already exists in local catalogue'
          : 'Failed to create dataset. Please try again.',
      );
      this.submitError.emit(detail);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async submitAdditionalInfo(assetId: string): Promise<void> {
    const items = this.metadataInfoRef()?.getAdditionalInfoItems() ?? [];
    for (const item of items) {
      try {
        await this.catalogueApi.postProviderFeedback({
          assetId,
          type: item.type,
          data: item.data,
        });
      } catch (error) {
        console.error('Failed to submit additional info item:', error);
      }
    }
  }

  private resetWizard(): void {
    this.basicInfoRef()?.resetForm();
    this.metadataInfoRef()?.resetForm();
    this.basicValid.set(false);
    this.metadataValid.set(false);
    this.basicInfo.set(null);
    this.metadataInfo.set(null);
    this.createdItemId.set(null);
    this.stacCollectionId.set(null);
    this.createdDatasetResult.set(null);
    this.currentStep.set(0);
    this.syncStepQueryParam();
  }

  private syncStepQueryParam(): void {
    const step = this.currentStep();
    const next = String(step);
    if (this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY) === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [STEP_QUERY_KEY]: step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
